/**
 * VHAKACHA LISTING UPDATES  (web app)
 * ============================================================
 * Short posts from an operator, shown on the listing page: a closure, a
 * refurbishment, a new departure time.
 *
 * Stored at `listings/{listingId}/updates/{updateId}`, world-readable, and
 * writable only by whoever may edit the listing — enforced in
 * firestore.rules, which reads ownership off the PARENT listing rather than
 * trusting a claim on the post. Nothing here is a security boundary.
 *
 * WHY A SUBCOLLECTION AND NOT AN ARRAY ON THE LISTING
 * --------------------------------------------------
 * Posts accumulate and are never edited together. An array field would be
 * rewritten in full on every post, which is both a lost-update race between
 * an owner and a manager posting at once, and a document that grows without
 * bound in every read of the listing — including the home feed, which reads
 * every published listing.
 *
 * THE UNREAD MARK IS PER DEVICE, ON PURPOSE
 * -----------------------------------------
 * Most travellers here are anonymous, so there is no account to hang "last
 * seen" on. localStorage gets it right for the common case and degrades to
 * "everything looks new" in a private window, which is the harmless direction
 * to be wrong in. A signed-in-only dot would be absent for most of the people
 * it exists for.
 * ============================================================
 */

import { collection, getDocs, orderBy, query, limit } from 'firebase/firestore';
import { db } from '../config/firebase';

/** Most recent first, and enough of them that "See more" is never needed. */
const MAX_UPDATES = 20;

const LAST_SEEN_KEY = 'vhakacha:updates-seen';

/** Milliseconds → a comparable number, from whatever the document carries. */
function toMillis(value) {
  if (!value) return 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  // Firestore Timestamp, in either of the two shapes a snapshot can produce.
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
}

/**
 * The posts on a listing, newest first.
 *
 * A failure to read is not fatal: the listing is the thing the traveller came
 * for, and a page that 500s because a notice board is unavailable is worse
 * than one with no notice board. Returns [] and logs, as `getListingReviews`
 * does.
 */
export async function getListingUpdates(listingId) {
  if (!listingId) return [];

  try {
    const snapshot = await getDocs(
      query(
        collection(db, 'listings', listingId, 'updates'),
        orderBy('createdAt', 'desc'),
        limit(MAX_UPDATES)
      )
    );

    return snapshot.docs
      .map((entry) => {
        const data = entry.data() || {};
        return {
          id: entry.id,
          title: typeof data.title === 'string' ? data.title.trim() : '',
          body: typeof data.body === 'string' ? data.body.trim() : '',
          createdAt: data.createdAt ?? null,
          createdAtMillis: toMillis(data.createdAt),
        };
      })
      // A post with no title cannot be rendered as anything a reader would
      // recognise as a post. The rules refuse to store one; this covers rows
      // written before they did.
      .filter((update) => update.title);
  } catch (error) {
    console.error('Failed to read updates for listing', listingId, error);
    return [];
  }
}

/** When this device last opened the Updates tab for a listing. */
export function lastSeenAt(listingId) {
  if (!listingId || typeof window === 'undefined') return 0;
  try {
    const stored = JSON.parse(window.localStorage.getItem(LAST_SEEN_KEY) || '{}');
    const seen = stored[listingId];
    return typeof seen === 'number' ? seen : 0;
  } catch (error) {
    // A corrupted or unavailable store means "never seen", which shows the
    // dot. Better than throwing on a page that is otherwise fine.
    return 0;
  }
}

/** Records that this device has now read them. */
export function markUpdatesSeen(listingId, at = Date.now()) {
  if (!listingId || typeof window === 'undefined') return;
  try {
    const stored = JSON.parse(window.localStorage.getItem(LAST_SEEN_KEY) || '{}');
    stored[listingId] = at;
    window.localStorage.setItem(LAST_SEEN_KEY, JSON.stringify(stored));
  } catch (error) {
    // Private browsing, or a full quota. The dot staying on is a cosmetic
    // failure and not worth surfacing to a traveller.
  }
}

/**
 * Is there a post this device has not seen?
 *
 * A listing whose posts are all older than the last visit shows no dot. A
 * device that has never opened the tab shows one as soon as there is anything
 * to read — which is the point: the dot means "there is something here", not
 * "something changed since you last looked at this exact post".
 */
export function hasUnseenUpdates(updates, listingId) {
  if (!Array.isArray(updates) || !updates.length) return false;
  const seen = lastSeenAt(listingId);
  return updates.some((update) => update.createdAtMillis > seen);
}
